import React, { useCallback } from "react";
import { useEditor } from "@craftjs/core";

import {
  BLOCK_WIDGETS,
  captureInsertDescriptor,
  captureRejectionReason,
} from "../captureToBlock";
import {
  TextWidget,
  ImageWidget,
  VideoWidget,
  AudioWidget,
  DocumentWidget,
} from "../../../components/SegmentBuilder/CodeBlocks";

/**
 * useInsertCaptureIntoSegment — put a captured item straight into the OPEN
 * Segment, as the block its own type implies.
 *
 * This is the mechanical half of Kelly's ask. The decision of WHICH block and
 * WHERE lives in the pure `captureToBlock` module, asserted on its own; this
 * hook only performs it against craft.js, so a failure here is unambiguously a
 * wiring failure and not a mapping one.
 *
 * WHY `addNodeTree(tree, "ROOT")` with no index: craft appends to the end of
 * the parent's child list. "ROOT" is the open Segment's canvas, so the new
 * block lands at the VERY BOTTOM of whatever the Segment already contains —
 * Kelly's explicit, non-negotiable placement rule. Passing an index would be
 * the way to prepend, and we deliberately never do.
 *
 * This is the SAME idiom the block palette already uses for click-to-add
 * (`Shared/Card/Card.jsx`), so the node tree produced is identical to the one a
 * hand-dragged block makes — not a second, divergent construction path.
 */

const COMPONENT_BY_WIDGET = {
  [BLOCK_WIDGETS.TEXT]: TextWidget,
  [BLOCK_WIDGETS.IMAGE]: ImageWidget,
  [BLOCK_WIDGETS.VIDEO]: VideoWidget,
  [BLOCK_WIDGETS.AUDIO]: AudioWidget,
  [BLOCK_WIDGETS.DOCUMENT]: DocumentWidget,
};

/** Result shapes, so a caller never has to interpret a bare boolean. */
export const INSERT_RESULT = {
  OK: "ok",
  REJECTED: "rejected",
  FAILED: "failed",
};

const useInsertCaptureIntoSegment = () => {
  const { actions, query } = useEditor();

  return useCallback(
    (item) => {
      const descriptor = captureInsertDescriptor(item);

      // A refusal is ALWAYS spoken. Kelly's recorded attempt produced complete
      // silence — no ghost, no highlight, no error — and silence reads as
      // broken. That silence is what caused three duplicate reports, so a
      // rejected drop must carry its reason back to the caller to render.
      if (!descriptor) {
        return { status: INSERT_RESULT.REJECTED, message: captureRejectionReason(item) };
      }

      const Component = COMPONENT_BY_WIDGET[descriptor.widget];
      if (!Component) {
        return {
          status: INSERT_RESULT.REJECTED,
          message: "This capture type can't be added to a Segment yet.",
        };
      }

      try {
        const element = React.createElement(Component, descriptor.props);
        const nodeTree = query.parseReactElement(element).toNodeTree();
        // No index argument => append. See the WHY note above.
        actions.addNodeTree(nodeTree, "ROOT");
        return { status: INSERT_RESULT.OK, widget: descriptor.widget };
      } catch (err) {
        // Never swallow: the user gets a message and the console gets the cause.
        console.error("Capture insert failed:", err);
        return {
          status: INSERT_RESULT.FAILED,
          message: "Couldn't add this to the Segment. Please try again.",
        };
      }
    },
    [actions, query]
  );
};

export default useInsertCaptureIntoSegment;
